import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { OrgUser } from '../types/admin';

interface OrgUsersResponse {
  success: boolean;
  data: OrgUser[];
}

interface OrgUserResponse {
  success: boolean;
  data: OrgUser;
}

/** What the invite endpoint did with the email that was submitted. */
export interface InviteOutcome {
  /** false when a brand-new invited record was created. */
  existing: boolean;
  /** Status of the pre-existing record before this invite (only when `existing`). */
  previousStatus?: NonNullable<OrgUser['status']>;
  /** Whether the pre-existing record's role was changed to the newly selected one. */
  roleChanged?: boolean;
}

export interface InviteUserResult {
  user: OrgUser;
  outcome: InviteOutcome;
}

interface InviteUserResponse extends OrgUserResponse {
  outcome?: InviteOutcome;
}

interface RemoveUserResponse {
  success: boolean;
  /** true when the record was deleted outright (never-activated member). */
  deleted?: boolean;
}

export interface RemoveUserResult {
  deleted: boolean;
}

const ROLE_LABELS: Record<OrgUser['role'], string> = {
  admin: 'Admin',
  estimator: 'Estimator',
  mover: 'Mover',
  dispatcher: 'Dispatcher',
  field_lead: 'Field Lead',
};

/**
 * Human-readable summary of an invite result, suitable for a toast.
 * e.g. "john@email.com already had an account. Their role was updated to
 * Estimator and a new invite has been sent."
 */
export function describeInviteOutcome({ user, outcome }: InviteUserResult): string {
  const email = user.email;
  const roleLabel = ROLE_LABELS[user.role] ?? user.role;
  if (!outcome.existing) return `Invite sent to ${email}.`;

  const roleNote = outcome.roleChanged ? ` Their role was updated to ${roleLabel} and` : '';
  switch (outcome.previousStatus) {
    case 'invited':
      return outcome.roleChanged
        ? `${email} already had a pending invite.${roleNote} a new invite has been sent.`
        : `${email} already had a pending invite, so a new invite has been sent.`;
    case 'inactive':
      return `${email} was inactive and has been reactivated as ${roleLabel}. A new invite has been sent.`;
    default:
      return outcome.roleChanged
        ? `${email} already had an account.${roleNote} a new invite has been sent.`
        : `${email} already had an account, so a new invite has been sent.`;
  }
}

export function useOrgAdminUsers(orgId: string) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<OrgUsersResponse>(`/org-admin/${orgId}/users`)
      .then(res => {
        if (!cancelled) {
          setUsers(res.data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [orgId]);

  /**
   * Invite a user by email. The backend reuses an existing member record with
   * the same email (active, invited, or inactive) instead of creating a
   * duplicate, updating the role and re-sending the invite. The returned
   * `outcome` says which case applied so the caller can tell the admin.
   */
  const inviteUser = useCallback(
    async (email: string, role: OrgUser['role']): Promise<InviteUserResult> => {
      const res = await api.post<InviteUserResponse>(`/org-admin/${orgId}/users`, { email, role });
      // Existing record reused → update in place; brand-new record → append
      setUsers(prev => {
        const exists = prev.some(u => u.uid === res.data.uid);
        return exists ? prev.map(u => u.uid === res.data.uid ? res.data : u) : [...prev, res.data];
      });
      return { user: res.data, outcome: res.outcome ?? { existing: false } };
    },
    [orgId],
  );

  const updateRole = useCallback(
    async (uid: string, role: OrgUser['role']) => {
      const res = await api.patch<OrgUserResponse>(`/org-admin/${orgId}/users/${uid}`, { role });
      setUsers(prev => prev.map(u => u.uid === uid ? res.data : u));
      return res.data;
    },
    [orgId],
  );

  /**
   * Remove a member. The backend deletes members who never activated (pending
   * invites) and deactivates everyone else; `deleted` says which happened.
   */
  const removeUser = useCallback(
    async (uid: string): Promise<RemoveUserResult> => {
      const res = await api.del<RemoveUserResponse>(`/org-admin/${orgId}/users/${uid}`);
      const deleted = res.deleted === true;
      setUsers(prev => deleted
        ? prev.filter(u => u.uid !== uid)
        : prev.map(u => u.uid === uid ? { ...u, status: 'inactive' as const } : u));
      return { deleted };
    },
    [orgId],
  );

  const activateUser = useCallback(
    async (uid: string) => {
      const res = await api.post<OrgUserResponse>(`/org-admin/${orgId}/users/${uid}/activate`, {});
      setUsers(prev => prev.map(u => u.uid === uid ? res.data : u));
      return res.data;
    },
    [orgId],
  );

  const resendInvite = useCallback(
    async (uid: string) => {
      await api.post(`/org-admin/${orgId}/users/${uid}/resend-invite`, {});
    },
    [orgId],
  );

  return { users, loading, error, inviteUser, updateRole, removeUser, activateUser, resendInvite };
}
